<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores'
import { useConfirmLogout } from '@/composables/useConfirmLogout'
import { getAvatarUrl } from '@/utils/imageUrl'
import { warnNavigation } from '@/utils/navigation-debug'
import ConfirmDialog from './ConfirmDialog.vue'
import FbKidAvatar from './ui/FbKidAvatar.vue'

withDefaults(defineProps<{ rail?: boolean }>(), { rail: false })

const authStore = useAuthStore()
const router = useRouter()
const avatarUrl = computed(() => getAvatarUrl(authStore.user?.avatar_key))
const { showPendingWarning, pendingCount, requestLogout, confirmLogout } = useConfirmLogout(handleLogout)

async function handleLogout() {
  try {
    await authStore.logout()
  } catch (error) {
    warnNavigation({
      prefix: '[AppBar]',
      event: 'logout:error',
      reason: error instanceof Error ? error.message : 'Unknown error',
    })
  }
  router.push('/login')
}
</script>

<template>
  <v-sheet
    data-testid="nav-profile"
    class="fb-nav-profile d-flex align-center ga-2"
    :class="{ 'flex-column': rail }"
    color="background"
  >
    <FbKidAvatar
      :name="authStore.user?.name ?? ''"
      :user-id="authStore.user?.id ?? ''"
      :image-url="avatarUrl"
      :size="40"
    />
    <v-sheet
      v-if="!rail"
      tag="span"
      color="transparent"
      class="fb-nav-profile-name text-truncate"
    >
      {{ authStore.user?.name }}
    </v-sheet>
    <v-btn
      icon="mdi-logout"
      variant="text"
      color="on-surface-variant"
      :width="44"
      :height="44"
      class="flex-shrink-0"
      data-testid="nav-sign-out"
      aria-label="Sign out"
      :loading="authStore.isLoading"
      @click="requestLogout"
    />
  </v-sheet>

  <ConfirmDialog
    v-model="showPendingWarning"
    title="Log out with unsynced completions?"
    :message="pendingCount > 0
      ? `${pendingCount} completions haven't synced and will be discarded if you log out.`
      : 'Unable to check for unsynced completions. Any completions that haven\'t synced will be discarded if you log out.'"
    confirm-label="Log out anyway"
    confirm-color="error"
    confirm-testid="confirm-logout-btn"
    :is-loading="authStore.isLoading"
    @confirm="confirmLogout"
  />
</template>

<style scoped>
.fb-nav-profile {
  width: 100%;
  padding: 10px;
  border-radius: 16px;
}

.fb-nav-profile-name {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 800;
}
</style>
